import { useEffect, useState } from "react";
import { read, utils } from "xlsx";
import { Button } from "./ui/button";
import {
  Column,
  doNotImport,
  shouldKeepRow,
  spreadSheetUploadSteps,
  validateSpreadSheetCell,
} from "../lib/spreadsheet-upload-config";
import UploadSpreadSheet from "./upload-spread-sheet";
import SpreadSheetColumnMapping from "./spread-sheet-column-mapping";
import SpreadSheetDataValidation from "./spread-sheet-data-validation";
import Stepper from "./stepper";

interface MappedColumn {
  supportedColumn: string;
  supportedColumnLabel: string;
  supportedColumnDataType: string;
  isRequired: boolean;
  originalColumn: string;
}

export interface SpreadSheetState {
  rowsData: Array<{ id: number;[key: string]: any }>;
  supportedColumns: Column[];
  mappedColumns: MappedColumn[];
  selectedFile: File | null;
}

interface SpreadSheetUploadFlowProps {
  supportedColumns: Column[];
  onUpload: (data: Array<{ checked: boolean;[key: string]: any }>) => void;
}

function SpreadSheetUploadFlow({
  supportedColumns,
  onUpload,
}: SpreadSheetUploadFlowProps) {
  const [uploadedSpreadSheet, setUploadedSpreadSheet] =
    useState<SpreadSheetState>({
      rowsData: [],
      supportedColumns: [],
      mappedColumns: [],
      selectedFile: null,
    });
  const [activeStep, setActiveStep] = useState<number>(
    spreadSheetUploadSteps[0].number
  );
  const [missingRequiredColumns, setMissingRequiredColumns] =
    useState<boolean>(true);
  const [countErrors, setCountErrors] = useState<number>(0);

  const disableNextButton = (): boolean => {
    if (activeStep === spreadSheetUploadSteps.length) {
      return countErrors > uploadedSpreadSheet.rowsData.length - countErrors;
    }
    return missingRequiredColumns;
  };

  const handleNext = (): void => {
    if (activeStep === spreadSheetUploadSteps.length) {
      const uploadedData = uploadedSpreadSheet.rowsData.map((item) => {
        const newItem: { checked: boolean;[key: string]: any } = {
          checked: false,
        };
        uploadedSpreadSheet.mappedColumns
          .filter((column) => column.supportedColumn !== "id")
          .filter((column) => column.supportedColumn !== doNotImport)
          .filter((column) => column.supportedColumn !== "")
          .forEach((column) => {
            newItem[column.supportedColumn] = item[column.originalColumn]
              ?.toString()
              ?.trim();
          });
        return newItem;
      });
      onUpload(uploadedData);
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handlePrevious = (): void => {
    if (activeStep >= 2) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const normalizeColumnLabel = (label: string) =>
    label.toLowerCase().replace(/\s+/g, " ").trim();

  const createMapping = (columns: string[]): MappedColumn[] => {
    const mapping = columns.map((item) => {
      const normalizedItem = normalizeColumnLabel(item);
      const supportedColumn = supportedColumns.find(
        (column) => normalizeColumnLabel(column.label) === normalizedItem
      );
      return {
        supportedColumn: supportedColumn?.value || "",
        supportedColumnLabel: supportedColumn?.label || "",
        supportedColumnDataType: supportedColumn?.type || "",
        isRequired: supportedColumn?.isRequired ?? true,
        originalColumn: item,
      };
    });
    return mapping;
  };

  const processDataOfUploadedFile = (file: File | null): void => {
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      if (!e.target?.result) return;

      const wb = read(e.target.result);
      const sheetName = wb.SheetNames[0];
      const sheet = wb.Sheets[sheetName];

      const rawData: any[][] = utils.sheet_to_json(sheet, { header: 1 });

      // Find the first row that looks like a valid header
      const headerRowIndex = rawData.findIndex((row) => {
        const nonEmpty = row.filter(
          (cell: any) => cell !== undefined && cell !== ""
        ).length;
        return nonEmpty >= 2; // You can tweak this based on how sparse headers are
      });

      if (headerRowIndex === -1) return;

      const header = rawData[headerRowIndex];
      const dataRows = rawData.slice(headerRowIndex + 1);

      // Reconstruct sheet as array of objects
      const structuredData = dataRows.map((row) => {
        const rowObject: any = {};
        header.forEach((key: string, i: number) => {
          rowObject[key] = row[i];
        });
        return rowObject;
      });

      const columns = header.map(String);
      const filteredRows = structuredData.filter((row) =>
        shouldKeepRow(row, columns)
      );

      const rowsWithId = filteredRows.map((row, index) => ({
        ...row,
        id: index + 1,
      }));
      const mappedColumns = createMapping(columns);

      const newInitialState: SpreadSheetState = {
        ...uploadedSpreadSheet,
        rowsData: rowsWithId,
        supportedColumns: supportedColumns,
        mappedColumns: mappedColumns,
        selectedFile: file,
      };

      setUploadedSpreadSheet(newInitialState);
      const hasMissingRequiredColumns = supportedColumns.some((column) => {
        return (
          column.isRequired &&
          mappedColumns.find(
            (mappedColumn) => mappedColumn.supportedColumn === column.value
          ) === undefined
        );
      });
      if (hasMissingRequiredColumns) {
        setActiveStep((prev) => prev + 1);
      } else {
        setActiveStep((prev) => prev + 2);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  useEffect(() => {
    const hasMissingRequiredColumns =
      uploadedSpreadSheet?.supportedColumns.some((column) => {
        return (
          column.isRequired &&
          uploadedSpreadSheet?.mappedColumns.find(
            (mappedColumn) => mappedColumn.supportedColumn === column.value
          ) === undefined
        );
      });
    setMissingRequiredColumns(hasMissingRequiredColumns);
  }, [uploadedSpreadSheet]);

  useEffect(() => {
    let countErrors = 0;
    let alreadyCountError = false;
    let arrayLength = uploadedSpreadSheet.rowsData.length - 1;
    uploadedSpreadSheet.rowsData.forEach((item, index) => {
      alreadyCountError = false;
      uploadedSpreadSheet.mappedColumns.forEach((column) => {
        let value = item[column.originalColumn]?.toString()?.trim();
        let invalidData = !validateSpreadSheetCell(
          column.supportedColumnDataType,
          value,
          column.isRequired
        );
        if (invalidData && !alreadyCountError) {
          countErrors = countErrors + 1;
          alreadyCountError = true;
        }
      });
      if (arrayLength === index) {
        setCountErrors(countErrors);
      }
    });
  }, [uploadedSpreadSheet]);

  return (
    <div className="flex h-full flex-col space-y-8">
      <Stepper steps={spreadSheetUploadSteps} activeStep={activeStep} />
      <div className="h-full flex-grow overflow-x-auto">
        {activeStep === spreadSheetUploadSteps[0].number && (
          <UploadSpreadSheet
            processDataOfUploadedFile={processDataOfUploadedFile}
          />
        )}
        {activeStep === spreadSheetUploadSteps[1].number && (
          <SpreadSheetColumnMapping
            uploadedSpreadSheet={uploadedSpreadSheet}
            setUploadedSpreadSheet={setUploadedSpreadSheet}
            missingRequiredColumns={missingRequiredColumns}
          />
        )}
        {activeStep === spreadSheetUploadSteps[2].number && (
          <SpreadSheetDataValidation
            uploadedSpreadSheet={uploadedSpreadSheet}
            setUploadedSpreadSheet={setUploadedSpreadSheet}
          />
        )}
      </div>
      {activeStep > spreadSheetUploadSteps[0].number && (
        <div className="flex justify-between px-6 pb-6">
          <Button variant="outline" onClick={handlePrevious}>
            Back
          </Button>
          <Button disabled={disableNextButton()} onClick={handleNext}>
            {activeStep === spreadSheetUploadSteps.length
              ? "Confirm"
              : "Continue"}
          </Button>
        </div>
      )}
    </div>
  );
}

export default SpreadSheetUploadFlow;
